import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { addDays, fromIsoDate, isValidIsoDate, toIsoDate } from '@/lib/dates';
import { fallbackRefetchInterval } from '@/lib/realtime';
import { useSocketStore } from '@/stores/socketStore';
import type { AttentePiecesItem, Intervention, Mechanic, TypeAgenda } from '@/lib/types';

export function useInterventionsByDate(date: string, typeAgenda?: TypeAgenda) {
  const connected = useSocketStore((s) => s.connected);
  return useQuery({
    queryKey: ['interventions', { date, typeAgenda }],
    queryFn: () =>
      api.get<{ interventions: Intervention[] }>(
        `/interventions?date=${date}${typeAgenda ? `&typeAgenda=${typeAgenda}` : ''}`,
      ),
    select: (data) => data.interventions,
    // Évite un GET avec date vide/NaN pendant un rendu transitoire (boot/login).
    enabled: isValidIsoDate(date),
    staleTime: 15_000,
    // Filet de sécurité : re-fetch périodique UNIQUEMENT si le socket temps réel
    // est tombé. Connecté, on s'appuie sur l'invalidation socket (pas de polling).
    refetchInterval: fallbackRefetchInterval(connected, 30_000),
  });
}

export function useInterventionsByWeek(weekStart: string, typeAgenda?: TypeAgenda) {
  const valid = isValidIsoDate(weekStart);
  const weekEnd = valid ? toIsoDate(addDays(fromIsoDate(weekStart), 6)) : weekStart;
  const connected = useSocketStore((s) => s.connected);
  return useQuery({
    queryKey: ['interventions', { week: weekStart, typeAgenda }],
    queryFn: () =>
      api.get<{ interventions: Intervention[] }>(
        `/interventions?dateFrom=${weekStart}&dateTo=${weekEnd}${typeAgenda ? `&typeAgenda=${typeAgenda}` : ''}`,
      ),
    select: (data) => data.interventions,
    // Évite un GET avec dateFrom=NaN pendant un rendu transitoire (boot/login).
    enabled: valid,
    staleTime: 15_000,
    // Filet de sécurité : re-fetch périodique UNIQUEMENT si le socket temps réel
    // est tombé. Connecté, on s'appuie sur l'invalidation socket (pas de polling).
    refetchInterval: fallbackRefetchInterval(connected, 30_000),
  });
}

export function useAttentePieces() {
  const connected = useSocketStore((s) => s.connected);
  return useQuery({
    queryKey: ['interventions', 'attente-pieces'],
    queryFn: () => api.get<{ interventions: AttentePiecesItem[] }>('/interventions/attente-pieces'),
    select: (data) => data.interventions,
    staleTime: 15_000,
    refetchInterval: fallbackRefetchInterval(connected, 30_000),
  });
}

export function useMechanics() {
  return useQuery({
    queryKey: ['mecaniciens'],
    queryFn: () => api.get<{ mecaniciens: Mechanic[] }>('/mecaniciens'),
    select: (data) => data.mecaniciens,
    staleTime: 5 * 60_000,
  });
}
